import React, { forwardRef } from 'react';
import type { SwitchRootState } from '@base-ui/react/switch';
import { Switch as BaseSwitch } from '@base-ui/react/switch';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

import OptionalLabel from '../../internal/OptionalLabel';

type SwitchSize = 'small' | 'medium';

export const SIZES = {
  small: 'small',
  medium: 'medium',
} as const satisfies Record<SwitchSize, SwitchSize>;

/**
 * The change event handlers receive: the hidden input's own native change event, typed by what it
 * truly is. Reading `target.checked` is the contract; synthetic-event members (`persist`,
 * `nativeEvent`, ...) do not exist on it, and this type makes reaching for them a compile error.
 */
export type SwitchChangeEvent = BaseSwitch.Root.ChangeEventDetails['event'] & { target: HTMLInputElement };

export interface SwitchProps {
  /**
   * `undefined` on the first render mounts the switch uncontrolled, and the mode is latched: a
   * value that arrives later (`checked={config?.is_enabled}` before the config loads) is ignored,
   * with only a dev-mode warning. Pass a boolean from the first render for a controlled switch.
   */
  checked?: boolean;
  /**
   * Fired with the change event of the hidden `<input type="checkbox">`, whose `target.checked`
   * already holds the new value, so handlers may read either argument. `target` is a live
   * reference: read it inside the handler, before a controlled re-render resets the input.
   */
  onChange?: (event: SwitchChangeEvent, checked: boolean) => void;
  /** Runs before the internal toggle; several consumers use it as their toggle mechanism. */
  onClick?: (event: React.MouseEvent<HTMLSpanElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
  disabled?: boolean;
  className?: string;
  /** Applied to the hidden input, so an external `<label htmlFor>` keeps working. */
  id?: string;
  /** Identifies the field when a form is submitted. */
  name?: string;
  /** @default 'small' */
  size?: SwitchSize;
  /**
   * No consumer sets it today; it is declared because FormControlLabel (core/Atomic) clones its
   * control with a `required` key on every render — usually holding `undefined` — which the
   * unexpected-props guard would otherwise report. When actually set, it reaches the hidden
   * input (native constraint semantics) and `aria-required` on the switch.
   */
  required?: boolean;
  'aria-label'?: string;
  /**
   * A label rendered beside the switch, wired to it as `aria-labelledby`, so it also names the
   * switch for assistive technology and wins over `aria-label` when both are given. Clicking it
   * toggles. Use one labelling channel per switch: not alongside an external `<label htmlFor>`.
   */
  label?: React.ReactNode;
}

// The design system's switch geometry (small: 40x24 root, 26x10 track inset 7px, 16px thumb
// travelling 16px inside a 24px halo circle; medium: 58x38 root, 34x14 track inset 12px, 20px
// thumb travelling 20px inside a 38px halo). The track is the root's ::before, so the whole
// root stays the hit area and the layout footprint; the halo hosts the thumb and carries the
// travel; the whole root is also the hover trigger for the halo.
const rootSizeClasses = {
  small: 'h-24 w-40 before:inset-[7px]',
  medium: 'h-[38px] w-[58px] before:inset-12',
} as const satisfies Record<SwitchSize, string>;

// The travel reacts to the root's data-checked attribute rather than the checked prop, because
// two consumers render uncontrolled switches whose state changes without a prop changing.
const haloSizeClasses = {
  small: 'h-24 w-24 group-data-[checked]/switch:translate-x-16',
  medium: 'h-[38px] w-[38px] group-data-[checked]/switch:translate-x-20',
} as const satisfies Record<SwitchSize, string>;

const thumbSizeClasses = {
  small: 'h-16 w-16',
  medium: 'h-20 w-20',
} as const satisfies Record<SwitchSize, string>;

// The interaction halo derives from the root's text colour (teal-700 checked, grey-400
// unchecked — the Figma values): currentColor at 12% on hover and 20% on keyboard focus. A
// build-time opacity modifier cannot apply to `currentColor` (no channels to rewrite), so the
// blend is the browser's, via color-mix. Hover keys on the root group, not the halo itself,
// so the whole switch is the hover trigger rather than just the halo circle.
const haloInteractionClasses =
  'group-hover/switch:bg-[color-mix(in_srgb,currentColor_12%,transparent)] ' +
  'group-focus-visible/switch:bg-[color-mix(in_srgb,currentColor_20%,transparent)]';

// The soft elevation shadow keeps the white thumb visible on light backgrounds.
const thumbShadow = 'shadow-elevation-1';

const Switch = forwardRef<HTMLSpanElement, SwitchProps>(
  (
    {
      checked,
      onChange,
      onClick,
      onBlur,
      disabled,
      className,
      id,
      name,
      size = 'small',
      required,
      'aria-label': ariaLabel,
      label,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Switch: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the SwitchProps interface and to the component's signature.`,
        true
      );
    }

    const handleCheckedChange = (nextChecked: boolean, eventDetails: BaseSwitch.Root.ChangeEventDetails) => {
      // One truthful narrowing: the event always originates from the hidden input, so its target
      // is that input. (Base UI types `event` loosely as Event.)
      onChange?.(eventDetails.event as SwitchChangeEvent, nextChecked);
    };

    // Classes derive from Base UI's state callback rather than data-attribute variants: the two
    // uncontrolled consumers change state without a prop changing, and a JS conditional keeps the
    // checked+disabled combination deterministic instead of resting on stylesheet order.
    const rootClassName = (state: SwitchRootState) =>
      cn(
        'group/switch relative inline-block shrink-0 select-none align-middle outline-none',
        "before:absolute before:rounded-full before:bg-black before:opacity-[0.38] before:transition-[background-color,opacity] before:duration-150 before:content-['']",
        rootSizeClasses[size],
        state.checked && 'before:bg-current before:opacity-50',
        state.disabled ? 'cursor-default before:opacity-[0.12]' : 'cursor-pointer',
        {
          'text-teal-700': state.checked,
          'text-grey-400': !state.checked,
          'pointer-events-none': state.disabled,
        },
        className
      );

    const haloClassName = cn(
      'absolute left-0 top-0 rounded-full transition-[transform,background-color] duration-150 flex justify-center items-center',
      haloSizeClasses[size],
      // The prop decides this (disabled is never internal state), so the disabled halo needs no
      // specificity dance: the classes are simply absent.
      !disabled && haloInteractionClasses
    );

    const thumbClassName = (state: SwitchRootState) =>
      cn(
        'rounded-full',
        thumbShadow,
        thumbSizeClasses[size],
        state.checked ? 'bg-teal-700' : 'bg-white',
        state.disabled && (state.checked ? 'bg-teal-300' : 'bg-slate-200')
      );

    return (
      <OptionalLabel label={label} id={id} disabled={disabled} className={size === 'small' ? 'gap-4' : 'gap-0'}>
        <BaseSwitch.Root
          data-fs-component="switch"
          ref={ref}
          checked={checked === undefined ? undefined : Boolean(checked)}
          onCheckedChange={handleCheckedChange}
          onClick={onClick}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          id={id}
          name={name}
          aria-label={ariaLabel}
          className={rootClassName}
        >
          <span className={haloClassName}>
            <BaseSwitch.Thumb className={thumbClassName} />
          </span>
        </BaseSwitch.Root>
      </OptionalLabel>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
